import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Stack,
} from '@mui/material';
import {
  CalendarToday,
  NavigateNext,
  ArrowBack,
} from '@mui/icons-material';
import { CalendarBooking } from '@/types';

/**
 * Demo component to test calendar navigation functionality
 * This component demonstrates the navigation flow from calendar to booking details
 */
export const NavigationDemo: React.FC = () => {
  const navigate = useNavigate();
  const [demoStep, setDemoStep] = useState(0);

  // Mock booking data for demonstration
  const mockBooking: CalendarBooking = {
    bookingId: 'demo-booking-123',
    listingId: 'listing-1',
    hostId: 'host-1',
    guestId: 'guest-1',
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
    guests: 2,
    totalPrice: 300.00,
    status: 'confirmed',
    paymentStatus: 'paid',
    primaryGuestContact: {
      email: 'demo@example.com',
      
      whatsappNumber: '+1234567890',
      preferredMethod: 'email',
    },
    expectedGuests: 2,
    guestCheckInStatus: {
      completed: 1,
      pending: 1,
      lastUpdated: new Date().toISOString(),
    },
    communicationLog: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    listingTitle: 'Demo Apartment',
    isExternal: false,
    source: 'direct',
    displayColor: '#4caf50',
  };

  const handleNavigateToBooking = () => {
    // Simulate navigation from calendar with context preservation
    navigate(`/bookings/${mockBooking.bookingId}`, {
      state: {
        fromCalendar: true,
        selectedDate: new Date().toISOString(),
        currentMonth: new Date().toISOString(),
        selectedListings: ['listing-1'],
      },
    });
  };

  const handleNavigateToCalendar = () => {
    // Simulate navigation to calendar with URL parameters
    const params = new URLSearchParams({
      date: new Date().toISOString().split('T')[0],
      month: new Date().toISOString().split('T')[0],
      listings: 'listing-1',
    });
    navigate(`/calendar?${params.toString()}`);
  };

  const handleNavigateToCalendarWithState = () => {
    // Simulate returning to calendar with restored context
    navigate('/calendar', {
      state: {
        selectedDate: new Date().toISOString(),
        currentMonth: new Date().toISOString(),
        selectedListings: ['listing-1'],
        restoreContext: true,
      },
    });
  };

  const demoSteps = [
    {
      title: 'Step 1: Calendar View',
      description: 'User is viewing the calendar and clicks on a booking',
      action: 'Navigate to Booking Details',
      handler: handleNavigateToBooking,
    },
    {
      title: 'Step 2: Deep Linking',
      description: 'Navigate to calendar with URL parameters for deep linking',
      action: 'Navigate with URL Params',
      handler: handleNavigateToCalendar,
    },
    {
      title: 'Step 3: Context Restoration',
      description: 'Return to calendar with preserved state',
      action: 'Navigate with State',
      handler: handleNavigateToCalendarWithState,
    },
  ];

  const currentStep = demoSteps[demoStep];

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CalendarToday color="primary" />
        Calendar Navigation Demo
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        This demo shows the calendar navigation functionality implemented in Task 7.
        It demonstrates booking navigation with context preservation, URL parameter handling,
        and state restoration.
      </Alert>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {currentStep.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {currentStep.description}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            onClick={currentStep.handler}
            startIcon={<NavigateNext />}
          >
            {currentStep.action}
          </Button>

          {demoStep > 0 && (
            <Button
              variant="outlined"
              onClick={() => setDemoStep(demoStep - 1)}
              startIcon={<ArrowBack />}
            >
              Previous Step
            </Button>
          )}

          {demoStep < demoSteps.length - 1 && (
            <Button
              variant="outlined"
              onClick={() => setDemoStep(demoStep + 1)}
            >
              Next Step
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Implementation Features
        </Typography>
        <Stack spacing={1}>
          <Typography variant="body2">
            ✅ Click handlers on BookingCard components in day summary panel
          </Typography>
          <Typography variant="body2">
            ✅ Navigation to booking details page with context preservation
          </Typography>
          <Typography variant="body2">
            ✅ Browser history management to maintain calendar state
          </Typography>
          <Typography variant="body2">
            ✅ Return navigation that restores previous calendar state and selected date
          </Typography>
          <Typography variant="body2">
            ✅ URL parameter handling for deep linking to specific dates
          </Typography>
          <Typography variant="body2">
            ✅ Dedicated BookingDetail page with back navigation
          </Typography>
        </Stack>
      </Paper>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Mock Booking Data:</strong><br />
          ID: {mockBooking.bookingId}<br />
          Listing: {mockBooking.listingTitle}<br />
          Status: {mockBooking.status}<br />
          Dates: {new Date(mockBooking.startDate).toLocaleDateString()} - {new Date(mockBooking.endDate).toLocaleDateString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default NavigationDemo;